import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import type { LLMTabContentForDispatch } from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import type { CustomDashAssistantEntityType } from './global-ai-assistant-api';
import type {
  DashboardPageStreamEvent,
  GlobalAssistantConversationPayload,
} from './global-ai-run-page-stream';
import { runDashboardPageStreamCreate } from './global-ai-run-page-stream';

export type HeadlessGeneratedPage = {
  content: LLMTabContentForDispatch;
  filters?: TalentDiscoveryAiFilterSearchResponseV2;
};

/**
 * Run the create-mode page-gen stream HEADLESSLY (no mounted builder) and
 * collect the FINAL layout + resolved filters (resolve-first Phase C).
 * The dispatch is a no-op and the stream's own filter apply is captured
 * instead of executed — application happens later, on the seeded landing.
 *
 * Returns null on failure OR when the stream asked clarification questions
 * (callers fall back to their legacy flow — never a dead end). Progress
 * events are forwarded so each call site can render them its own way (the
 * turn runner routes them into the activity lane; the click-time path shows
 * a generation status).
 */
export async function generateDashboardHeadless(params: {
  prompt: string;
  entityType: CustomDashAssistantEntityType;
  conversation?: GlobalAssistantConversationPayload;
  onProgress?: (event: DashboardPageStreamEvent) => void;
  signal?: AbortSignal;
}): Promise<HeadlessGeneratedPage | null> {
  try {
    let lastConfig: LLMTabContentForDispatch | undefined;
    let lastFilters: TalentDiscoveryAiFilterSearchResponseV2 | undefined;
    let sawQuestions = false;
    await runDashboardPageStreamCreate({
      prompt: params.prompt,
      entityType: params.entityType,
      ...(params.conversation ? { conversation: params.conversation } : {}),
      // Committed build: the user chose to build, so the generation must
      // best-guess rather than bail to a clarifying question mid-build (the
      // `sawQuestions` fallback below then stays a pure safety net).
      allowClarification: false,
      dispatch: () => undefined,
      applyAiSearchFilters: () => Promise.resolve(),
      onEvent: (event) => {
        if (event.type === 'config') {
          lastConfig = event.content;
        } else if (event.type === 'filters') {
          lastFilters = event.content;
        } else if (event.type === 'questions') {
          sawQuestions = true;
        }
        params.onProgress?.(event);
      },
      ...(params.signal ? { signal: params.signal } : {}),
    });
    if (sawQuestions || lastConfig === undefined) {
      return null;
    }
    return {
      content: lastConfig,
      ...(lastFilters ? { filters: lastFilters } : {}),
    };
  } catch {
    return null;
  }
}
